import { reactive } from "vue";
import JSZip from "jszip";
import { ImageTransform } from "../types";
import { clamp } from "./clamp";

const canvases = reactive<Record<string, HTMLCanvasElement>>({});

const IMAGE_TYPE = "image/jpeg";
const IMAGE_QUALITY = 0.9;

export const useCanvases = () => {
  const registerCanvas = (name: string, canvas: HTMLCanvasElement): void => {
    canvases[name] = canvas;
  };

  const getCanvasBlob = (
    canvas: HTMLCanvasElement,
    type = IMAGE_TYPE,
    quality = IMAGE_QUALITY
  ): Promise<Blob | undefined> => {
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(blob ?? undefined);
        },
        type,
        quality
      );
    });
  };

  const downloadImage = async (image: HTMLImageElement, transform: ImageTransform) => {
    const { cropWidth, cropHeight, cropX, cropY } = transform;

    if (!cropWidth || !cropHeight) {
      return;
    }

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    if (!context) {
      throw Error("Couldn't fetch canvas context");
    }

    canvas.width = cropWidth;
    canvas.height = cropHeight;

    const scale = Math.max(cropWidth / image.naturalWidth, cropHeight / image.naturalHeight);
    const scaledWidth = Math.floor(image.naturalWidth * scale);
    const scaledHeight = Math.floor(image.naturalHeight * scale);

    const dx = Math.round(cropWidth / 2 - (image.naturalWidth / 2) * scale);
    const dy = Math.round(cropHeight / 2 - (image.naturalHeight / 2) * scale);

    // Calculate how to shift the dx and dy to centre the focus point as best as possible for the given aspect ratio
    // fx and fy repesent how would transform dx and dy in order to exactly center the focus point
    const fx = Math.round(scaledWidth / 2 - scaledWidth * cropX);
    const fy = Math.round(scaledHeight / 2 - scaledHeight * cropY);

    // Focussed dx and dy values fdx and fdy
    // It's not always possible to exactly centre a focus point as extreme focus values would draw most of the image off the canvas and leave empty space in the canvas with no image.
    const fdx = clamp(dx * 2, dx + fx, 0);
    const fdy = clamp(dy * 2, dy + fy, 0);

    // Draw the image on the canvas
    context.drawImage(image, fdx, fdy, scaledWidth, scaledHeight);

    const filename = `image-${cropWidth}x${cropHeight}.jpg`;

    return downloadCanvas(filename, canvas);
  };

  const downloadCanvas = (filename: string, canvas: HTMLCanvasElement) => {
    const link = document.createElement("a");
    link.download = filename;
    link.href = canvas.toDataURL();
    link.click();
    link.remove();
    canvas.remove();
  };

  const downloadFile = async (blob: Blob, filename: string): Promise<void> => {
    const link = document.createElement("a");
    link.download = filename;
    link.href = window.URL.createObjectURL(blob);
    link.click();
    link.remove();
  };

  const downloadAll = async () => {
    const zip = new JSZip();

    for (const [path, canvas] of Object.entries(canvases)) {
      const [folderName, fileName] = path.split("/");

      const folder = zip.folder(folderName);

      if (folder) {
        const fileNameWithExtension = fileName + ".jpg";

        const blob = await getCanvasBlob(canvas);

        if (blob) {
          folder.file(fileNameWithExtension, blob, { base64: true });
        }
      }
    }

    const zipBlob = await zip.generateAsync({ type: "blob" });
    downloadFile(zipBlob, "biteable.zip");
  };

  const downloadSingle = async (name: string) => {
    const canvas = canvases[name];

    if (!canvas) {
      return;
    }

    const blob = await getCanvasBlob(canvas, "image/jpeg", 0.9);
    const filename = name.replace("/", "-") + ".jpg";

    if (blob) {
      downloadFile(blob, filename);
    }
  };

  return {
    canvases,
    registerCanvas,
    downloadSingle,
    downloadImage,
    downloadAll
  };
};
